import { useGlobalStore } from "../context/UserContext/userContext";
import { CRE_EXTENSION_PATH } from "../constants/routes";
import { buildCrePatientPath } from "../utils/buildPatientAuthUrl";
import { getCreAccessToken } from "./creAuthConfig";
import { getCreActiveAccount } from "./creAccountUtils";
import {
  clearCreLoginIntent,
  getCreLoginIntent,
  peekOrSetCreLoginIntent,
} from "./creLoginIntent";
import {
  clearMedmicReturnRoute,
  resolveMedmicReturnRoute,
} from "./creMedmicReturnRoute";
import { markMedMicExplicitConnect } from "./medmicExplicitConnect";
import { MedMicAuthCoordinator } from "./medmicAuthCoordinator";
import { getSafeTokenMeta, logMedmicAuthDiag } from "./creTokenDiagnostics";
import { getCreMsalInstance, initializeCreMsal } from "./creMsalInstance";

/**
 * Finish CRE sign-in after Entra redirects.
 * Extension mode: return to allowlisted MedMic route; Validate/connect runs on
 * the extension surface (Gate/Handoff). Do not run Enterprise patient landing.
 * Enterprise mode: Validate into Zustand only — no MedMic publish.
 *
 * @returns {Promise<
 *   | { ok: true; mode: "enterprise"; patientPath: string }
 *   | { ok: true; mode: "extension"; extensionPath: string }
 *   | { ok: false; reason: string }
 * >}
 */
export async function completeCreMsalLogin() {
  await initializeCreMsal();
  const instance = getCreMsalInstance();
  const account = getCreActiveAccount(instance);

  if (!account) {
    return { ok: false, reason: "no_account" };
  }

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const onExtensionPath = pathname.startsWith(CRE_EXTENSION_PATH);
  const intent =
    getCreLoginIntent() ?? (onExtensionPath ? "extension" : "enterprise");
  clearCreLoginIntent();

  if (intent === "extension" || onExtensionPath) {
    peekOrSetCreLoginIntent("extension");

    // MSAL redirectUri is origin — often not /cre/extension*. Defer Validate
    // to Gate/Handoff so we do not fail connect() off-surface and fall through
    // to the Enterprise Report Engine landing.
    const extensionPath = resolveMedmicReturnRoute();
    markMedMicExplicitConnect();

    if (onExtensionPath) {
      const ok = await MedMicAuthCoordinator.connect({
        source: "complete_cre_msal_login_extension",
      });
      if (!ok) {
        logMedmicAuthDiag("medmic", "complete_login_validate_failed", {
          ...MedMicAuthCoordinator.getEpoch(),
        });
        return { ok: false, reason: "validate_failed" };
      }

      const snap = MedMicAuthCoordinator.getSnapshot();
      logMedmicAuthDiag("medmic", "complete_login_ok", {
        ...MedMicAuthCoordinator.getEpoch(),
        fingerprint: getSafeTokenMeta(snap.accessToken).fingerprint,
        extensionPath,
      });
    } else {
      logMedmicAuthDiag("medmic", "complete_login_defer_to_surface", {
        extensionPath,
      });
    }

    return {
      ok: true,
      mode: "extension",
      extensionPath,
    };
  }

  const token = await getCreAccessToken(instance, account);
  if (!token) {
    return { ok: false, reason: "no_token" };
  }

  const meta = getSafeTokenMeta(token);
  logMedmicAuthDiag("enterprise", "complete_login_validate_start", {
    fingerprint: meta.fingerprint,
    aud: meta.aud,
    exp: meta.exp,
  });

  const valid = await useGlobalStore.getState().initializeWithCreToken(token);
  if (!valid) {
    logMedmicAuthDiag("enterprise", "complete_login_validate_failed", {
      fingerprint: meta.fingerprint,
    });
    return { ok: false, reason: "validate_failed" };
  }

  // Enterprise: no MedMic publish. Drop any stray MedMic return route.
  clearMedmicReturnRoute();

  const { creUser, accessToken } = useGlobalStore.getState();
  const patientPath = buildCrePatientPath(accessToken || token, creUser);

  return { ok: true, mode: "enterprise", patientPath };
}
